import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import React, { FC, PropsWithChildren } from 'react';
import { useAtomValue } from 'jotai';
import { NotificationHandle, useNotification } from '@idk-web/core-ui';
import { ServiceType } from '@idk-web/api';
import { portalLoginStatus } from '@/state/portalAuth';
import {
  SafeMailMessageReceived,
  SafeMailMessageSigned,
  useEventListener,
} from '@/hooks/portal/ws/useEventListener';
import { useCompany } from '@/hooks/portal/useCompany';
import { DEFAULT_NOTIFICATION_AUTO_HIDE_MS } from '@/config';
import routes from '@/components/portal/routes';

export type EventAwareRouteProps = PropsWithChildren;

const EventAwareRoute: FC<EventAwareRouteProps> = ({ children }) => {
  const { t } = useTranslation();
  const company = useCompany();
  const notification = useNotification();
  const loginStatus = useAtomValue(portalLoginStatus);
  const location = useLocation();

  useEventListener<SafeMailMessageReceived>(
    'SAFEMAIL_MESSAGE_RECEIVED',
    (event) => {
      if (company.services.some((s) => s.type === ServiceType.SAFEMAIL)) {
        const isOnInboxPage = location.pathname.startsWith(
          routes.safemailInbox,
        );
        const duration = isOnInboxPage
          ? DEFAULT_NOTIFICATION_AUTO_HIDE_MS
          : undefined;

        let handle: NotificationHandle | null = null;
        const content = (
          <>
            {t('portal.notifications.safemail_message_received')}{' '}
            {!isOnInboxPage && (
              <Link
                to={{
                  pathname: routes.safemailInbox,
                  search: new URLSearchParams({
                    q: event.message.id,
                  }).toString(),
                }}
                onClick={() => handle?.close()}
              >
                {t('portal.notifications.safemail_message_view')}
              </Link>
            )}
          </>
        );
        handle = notification.show(content, 'success', duration);
      }
    },
    [t, loginStatus, company, location],
  );

  useEventListener<SafeMailMessageSigned>(
    'SAFEMAIL_MESSAGE_SIGNED',
    (event) => {
      if (company.services.some((s) => s.type === ServiceType.SAFEMAIL)) {
        const isOnInboxPage = location.pathname.startsWith(
          routes.safemailInbox,
        );
        const duration = isOnInboxPage
          ? DEFAULT_NOTIFICATION_AUTO_HIDE_MS
          : undefined;

        let handle: NotificationHandle | null = null;
        const content = (
          <>
            {t('portal.notifications.safemail_message_signed')}{' '}
            {!isOnInboxPage && (
              <Link
                to={{
                  pathname: routes.safemailInbox,
                  search: new URLSearchParams({
                    q: event.message.id,
                  }).toString(),
                }}
                onClick={() => handle?.close()}
              >
                {t('portal.notifications.safemail_message_view')}
              </Link>
            )}
          </>
        );
        handle = notification.show(content, 'success', duration);
      }
    },
    [t, loginStatus, company, location],
  );

  return <>{children}</>;
};

export default EventAwareRoute;
